// @ts-nocheck
/*
 * Port of session-replay/list.js (328 lines). BYTE-IDENTICAL.
 * Server-paginated session list with date-range + agent + min-duration +
 * status filters (server accepts `min_duration` and `status` query args).
 * v1 has the Analytics link commented out â€” kept commented here too so
 * this port matches v1's shipped UI exactly. Active sessions render a
 * green "Live" badge in place of duration. `chunk_count` gates the Watch
 * button (nothing to replay if the backend has no chunks yet).
 * PrimeReact Dropdown â†’ react-select; PrimeReact DataTable (lazy) â†’
 * react-bootstrap Table + manual Pagination (same swap as agent-activity).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const Pagination: any = ({ className, children, ...props }: any) => (
  <ul className={_cx('pagination', className)} {...props}>
    {children}
  </ul>
);
const _pagItem = (label: string, glyph: any) => ({
  active,
  disabled,
  onClick,
  children,
  ...rest
}: any) => (
  <li
    className={_cx('page-item', active && 'active', disabled && 'disabled')}
    aria-label={label}
    {...rest}
  >
    <button
      type="button"
      className="page-link"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children ?? glyph}
    </button>
  </li>
);
Pagination.Item = _pagItem('Page', null);
Pagination.First = _pagItem('First', '«');
Pagination.Prev = _pagItem('Previous', '‹');
Pagination.Next = _pagItem('Next', '›');
Pagination.Last = _pagItem('Last', '»');
const Tooltip: any = ({ children, id: _id, ...props }: any) => <span {...props}>{children}</span>;
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_SESSION_REPLAY_SESSIONS,
  API_SESSION_REPLAY_DELETE,
  API_AGENTS_LIST,
} from '@/service/url';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { formatDuration } from './replayUtils';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Play,
  RefreshCw,
  Settings2,
  Trash2,
} from 'lucide-react';

const STATUS_PILLS: Record<string, { label: string; cls: string }> = {
  active: {
    label: 'Live',
    cls: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800',
  },
  ended: {
    label: 'Ended',
    cls: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700',
  },
  stale_closed: {
    label: 'Stale',
    cls: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800',
  },
};

const defaultStart = () => moment().startOf('month').format('YYYY-MM-DD');
const defaultEnd = () => moment().format('YYYY-MM-DD');

const PAGE_SIZE = 25;

const DURATION_OPTIONS = [
  { label: 'Any duration', value: null },
  { label: '> 1 minute', value: 60 },
  { label: '> 5 minutes', value: 300 },
  { label: '> 15 minutes', value: 900 },
  { label: '> 30 minutes', value: 1800 },
];

const STATUS_OPTIONS = [
  { label: 'All statuses', value: null },
  { label: 'Active', value: 'active' },
  { label: 'Ended', value: 'ended' },
  { label: 'Closed (stale)', value: 'stale_closed' },
];

function SessionReplayList() {
  const navigate = useNavigate();

  const [startDate, setStartDate] = usePersistedState('session-replay/SessionReplayList:startDate', defaultStart());
  const [endDate, setEndDate] = usePersistedState('session-replay/SessionReplayList:endDate', defaultEnd());
  const [agentId, setAgentId] = usePersistedState<any>('session-replay/SessionReplayList:agentId', null);
  const [minDuration, setMinDuration] = usePersistedState<any>('session-replay/SessionReplayList:minDuration', null);
  const [status, setStatus] = usePersistedState<any>('session-replay/SessionReplayList:status', null);

  const [agents, setAgents] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePersistedState('session-replay/SessionReplayList:page', 1);
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const replayAcl = useModuleAcl(MODULE_ACL.SESSION_REPLAY);
  const role = getUser()?.role;
  const canDelete =
    role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN || replayAcl.canDelete;

  useEffect(() => {
    (async () => {
      try {
        const resp: any = await getApiCall(API_AGENTS_LIST, { fields: 'min' });
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        const opts = list
          .map((a: any) => ({
            label:
              `${a.agent_name || a.name || 'Agent'}` +
              (a.agent_number ? ` (${a.agent_number})` : ''),
            value: a.id,
          }))
          .filter((o: any) => o.value != null);
        setAgents([{ label: 'All Agents', value: null }, ...opts]);
      } catch (e) {
        setAgents([{ label: 'All Agents', value: null }]);
      }
    })();
  }, []);

  const fetchData = useCallback(
    async (targetPage = 1) => {
      if (!startDate || !endDate) {
        toast.error('Select both start and end dates');
        return;
      }
      if (moment(startDate).isAfter(moment(endDate))) {
        toast.error('Start date cannot be after end date');
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          page: String(targetPage),
          limit: String(PAGE_SIZE),
        });
        if (agentId) params.append('agent_id', String(agentId));
        if (minDuration) params.append('min_duration', String(minDuration));
        if (status) params.append('status', status);

        const resp: any = await getApiCall(
          `${API_SESSION_REPLAY_SESSIONS}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(Number(resp?.total) || 0);
        setPage(Number(resp?.page) || targetPage);
      } catch (e: any) {
        toast.error(friendlyApiError(e) || 'Failed to load sessions');
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, agentId, minDuration, status],
  );

  useEffect(() => setSelected(new Set()), [rows]);

  const selectableRows = rows.filter((r: any) => r.status !== 'active');
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r: any) => selected.has(r.session_id));
  const toggleOne = (sid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableRows.map((r: any) => r.session_id)));

  const confirmDelete = async () => {
    if (!pendingDelete?.length) return;
    setDeleting(true);
    try {
      const resp: any = await postApiCall(API_SESSION_REPLAY_DELETE, {
        session_ids: pendingDelete.map((r: any) => r.session_id),
      });
      const deleted = Number(resp?.deleted) || 0;
      const skipped = (Number(resp?.skipped_live) || 0) + (Number(resp?.skipped_other) || 0);
      toast.success(
        `${deleted} session replay${deleted === 1 ? '' : 's'} deleted` +
          (skipped ? ` · ${skipped} skipped (live or unavailable)` : ''),
      );
      const emptiesPage = deleted >= rows.length;
      setPendingDelete(null);
      fetchData(emptiesPage && page > 1 ? page - 1 : page);
    } catch (e: any) {
      toast.error(friendlyApiError(e) || 'Failed to delete session replay');
    } finally {
      setDeleting(false);
    }
  };

  // Mount restores the saved page; a filter change goes back to page 1.
  const filterSig = JSON.stringify([agentId, minDuration, status]);
  const prevFilterSigRef = useRef(filterSig);
  useEffect(() => {
    const target = prevFilterSigRef.current === filterSig ? page : 1;
    prevFilterSigRef.current = filterSig;
    fetchData(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, minDuration, status]);

  const selectedAgentOption =
    agents.find((o: any) => o.value === agentId) || (agents.length ? agents[0] : null);
  const selectedDurationOption =
    DURATION_OPTIONS.find((o) => o.value === minDuration) || DURATION_OPTIONS[0];
  const selectedStatusOption =
    STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 40,
      height: 40,
      fontSize: 12,
      borderRadius: 6,
      border: '1px solid hsl(var(--border) / 0.6)',
      backgroundColor: 'hsl(var(--card))',
      boxShadow: state.isFocused
        ? 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9), 0 0 0 3px hsl(var(--ring) / 0.15)'
        : 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
      '&:hover': { borderColor: 'hsl(var(--border))' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 8px', height: 38 }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0, color: 'hsl(var(--foreground))' }),
    singleValue: (base: any) => ({ ...base, color: 'hsl(var(--foreground))' }),
    placeholder: (base: any) => ({ ...base, color: 'hsl(var(--muted-foreground))' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: 6,
      color: 'hsl(var(--muted-foreground))',
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: 8,
      border: '1px solid hsl(var(--border) / 0.6)',
      backgroundColor: 'hsl(var(--card))',
      color: 'hsl(var(--foreground))',
      boxShadow:
        '10px 10px 24px rgba(163, 177, 198, 0.4), -10px -10px 24px rgba(255, 255, 255, 0.9)',
      overflow: 'hidden',
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      padding: '8px 12px',
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary) / 0.15)'
        : state.isFocused
          ? 'hsl(var(--primary) / 0.08)'
          : 'transparent',
      color: state.isSelected ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
      cursor: 'pointer',
    }),
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Session Replay"
        description="Recorded agent-side sessions with playback, journeys, and analytics."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchData(page)}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={'h-4 w-4' + (loading ? ' animate-spin' : '')} />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/session-replay/recording-settings')}
            >
              <Settings2 className="h-4 w-4" />
              Recording Settings
            </Button>
          </div>
        }
      />
      {/* Filters */}
      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <DateRangeFilter
          value={{ from: startDate, to: endDate }}
          onChange={({ from, to }) => {
            setStartDate(from);
            setEndDate(to);
          }}
          onApply={() => fetchData(1)}
          applyLabel={loading ? 'Loading…' : 'Apply'}
          disabled={loading}
          fromLabel="Start Date"
          toLabel="End Date"
          max={defaultEnd()}
        >
          <div className="w-[220px]">
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <Select
              value={selectedAgentOption}
              options={agents}
              onChange={(opt: any) => setAgentId(opt?.value ?? null)}
              placeholder="All Agents"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div className="w-[170px]">
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Duration
            </label>
            <Select
              value={selectedDurationOption}
              options={DURATION_OPTIONS}
              onChange={(opt: any) => setMinDuration(opt?.value ?? null)}
              placeholder="Any duration"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div className="w-[160px]">
            <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <Select
              value={selectedStatusOption}
              options={STATUS_OPTIONS}
              onChange={(opt: any) => setStatus(opt?.value ?? null)}
              placeholder="All statuses"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
        </DateRangeFilter>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {canDelete && selected.size > 0 && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2 text-xs">
              <span className="text-muted-foreground">
                Selected: <span className="font-semibold text-foreground">{selected.size}</span>
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() =>
                    setPendingDelete(rows.filter((r: any) => selected.has(r.session_id)))
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete selected
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-primary/[0.06]">
                  <tr>
                    {canDelete && (
                      <th className="w-10 py-2.5 pl-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 cursor-pointer accent-primary"
                          checked={allSelected}
                          disabled={selectableRows.length === 0}
                          onChange={toggleAll}
                          aria-label="Select all ended sessions on this page"
                          title="Select all ended sessions on this page"
                        />
                      </th>
                    )}
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Agent / Started
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Status / Duration
                    </th>
                    <th className="w-32 px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={canDelete ? 4 : 3}
                        className="px-3 py-10 text-center text-sm text-muted-foreground"
                      >
                        No recorded sessions found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row: any, idx: number) => {
                      const a = row.agent || {};
                      const isStripe = idx % 2 === 1;
                      const isLive = row.status === 'active';
                      const pill = STATUS_PILLS[row.status] || {
                        label: row.status || 'Unknown',
                        cls: STATUS_PILLS.ended.cls,
                      };
                      return (
                        <tr
                          key={row.id}
                          className={
                            'border-t border-border/50 transition-colors hover:bg-primary/[0.06] ' +
                            (isStripe ? 'bg-muted/30' : 'bg-card')
                          }
                        >
                          {canDelete && (
                            <td className="w-10 py-2.5 pl-4">
                              <input
                                type="checkbox"
                                className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                                checked={selected.has(row.session_id)}
                                disabled={isLive}
                                onChange={() => toggleOne(row.session_id)}
                                aria-label="Select session"
                                title={isLive ? 'Live session cannot be deleted' : 'Select session'}
                              />
                            </td>
                          )}
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-foreground">
                              {a.agent_name || `Agent #${row.agent_id}`}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              Started:{' '}
                              <span className="font-mono text-foreground">
                                {row.started_at
                                  ? moment(row.started_at).format('YYYY-MM-DD HH:mm:ss')
                                  : '—'}
                              </span>
                            </div>
                            {a.agent_number ? (
                              <div className="text-xs text-muted-foreground">
                                Agent no.:{' '}
                                <span className="font-mono text-foreground">{a.agent_number}</span>
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={
                                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ' +
                                pill.cls
                              }
                            >
                              {isLive && (
                                <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                              )}
                              {pill.label}
                            </span>
                            {!isLive && (
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                Duration:{' '}
                                <span className="font-mono tabular-nums text-foreground">
                                  {formatDuration(row.duration_seconds)}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              type="button"
                              disabled={!row.chunk_count}
                              onClick={() =>
                                navigate(`/session-replay/watch/${row.session_id}`)
                              }
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
                              title={row.chunk_count ? 'Watch replay' : 'No recording chunks available'}
                              aria-label="Watch replay"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                disabled={isLive}
                                onClick={() => setPendingDelete([row])}
                                className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20 transition-colors hover:bg-destructive/15 disabled:cursor-not-allowed disabled:opacity-40 dark:text-red-400"
                                title={isLive ? 'Live session cannot be deleted' : 'Delete recording'}
                                aria-label="Delete recording"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > PAGE_SIZE && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-1 pb-2 pt-3 text-xs text-muted-foreground">
              <span>
                Page{' '}
                <span className="font-medium text-foreground">{page}</span> of{' '}
                <span className="font-medium text-foreground">{totalPages}</span>
                {' · '}
                <span className="font-medium text-foreground">{total}</span> total
              </span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(totalPages)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Last page"
                >
                  Last
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
        title={
          pendingDelete?.length > 1
            ? `Delete ${pendingDelete.length} session recordings?`
            : 'Delete this session recording?'
        }
        description={
          pendingDelete?.length > 1 ? (
            <>
              <span className="font-medium text-foreground">{pendingDelete.length}</span> selected
              sessions will be permanently deleted, including their playback and click/screen
              events. This cannot be undone.
            </>
          ) : (
            <>
              Session of{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.[0]?.agent?.agent_name || `Agent #${pendingDelete?.[0]?.agent_id}`}
              </span>{' '}
              started{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.[0]?.started_at
                  ? moment(pendingDelete[0].started_at).format('YYYY-MM-DD HH:mm:ss')
                  : '—'}
              </span>{' '}
              will be permanently deleted, including its playback and click/screen events. This
              cannot be undone.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </ScrollPage>
  );
}

export default SessionReplayList;
